import {
  TAddressInput,
  TBpjsInput,
  TEKTPInput,
  TEmergencyContactInput,
} from '@/src/modules/user/data/schemas/input';
import { RoutesApi } from '@/src/shared/constants/route';
import {
  EBloodType,
  EBloodTypeLabel,
  ERhesusType,
} from '@/src/shared/enums/blood-type';
import { EGenderEnglishLabel } from '@/src/shared/enums/gender';
import { EMaritalStatusEnglishLabel } from '@/src/shared/enums/marital-status';
import { ERelation } from '@/src/shared/enums/relation';
import { EReligionLabel } from '@/src/shared/enums/religion';
import { findValueFromOption, getEnumValue } from '@/src/shared/utils/common';
import { fetchLocation } from '@/src/shared/utils/fetch_location';
import { safeParseZodObject } from '@/src/shared/utils/parser';
import { MAddress } from '../../models/address';
import { MEmergencyContact } from '../../models/emergency-contact';
import { MIndividual } from '../../models/individual';
import { MUser } from '../../models/user';
import { TEmergencyContactResponse } from '../schemas/emergency-contact';
import { TIndividualResponse } from '../schemas/individual';
import {
  SAddressInputRequest,
  SBpjsInputRequest,
  SEKTPInputRequest,
  SEmergencyContactInputRequest,
  STorajaDeleteRequest,
  TAddressInputRequest,
  TBpjsInputRequest,
  TEKTPInputRequest,
  TEmergencyContactInputRequest,
  TTorajaDeleteRequest,
} from '../schemas/request';
import { TUploadKtpResponse } from '../schemas/response';
import { TUserResponse } from '../schemas/user';

export function mapAddresstoAddressCreateRequest(
  input: TAddressInput,
  addressId: number | null,
  individualId: number | undefined
): TAddressInputRequest {
  return safeParseZodObject<TAddressInputRequest>(SAddressInputRequest, {
    delete: [],
    upsert: [
      {
        model: 'MemberAddress',
        data: [
          {
            id: addressId,
            is_main_address: input.is_main_address,
            fk_model: {
              member: individualId,
            },
            type: Number(input.type_address),
            place: input.place_name,
            recipient_name: input.receiver_name,
            phone_number: input.phone_number,
            address: input.address,
            master_provinsi_code: input.province,
            master_kota_kabupaten_code: input.city,
            master_kecamatan_code: input.district,
            master_kelurahan_code: input.village,
            rt: input.rt,
            rw: input.rw,
            zip_code: input.postal_code,
          },
        ],
      },
      {
        model: 'DummyModel',
        data: [
          {
            id: null,
            locations: [
              {
                longitude: input.lng,
                latitude: input.lat,
              },
            ],
            fk_model: null,
          },
        ],
      },
    ],
  });
}

export function mapTorajaDeleteRequest(
  id: number,
  model: string
): TTorajaDeleteRequest {
  return safeParseZodObject<TTorajaDeleteRequest>(STorajaDeleteRequest, {
    delete: [
      {
        id: [id],
        form_index: null,
        model,
      },
    ],
    upsert: [],
  });
}

export function mapUpdateMainAddressRequest(
  address: MAddress,
  individualId: number | undefined
): TAddressInputRequest {
  return safeParseZodObject<TAddressInputRequest>(SAddressInputRequest, {
    delete: [],
    upsert: [
      {
        model: 'MemberAddress',
        data: [
          {
            id: address.id,
            is_main_address: true,
            fk_model: {
              member: individualId,
            },
            type: address.type,
            place: address.place,
            recipient_name: address.recipient_name,
            phone_number: address.phone_number,
            address: address.address,
            master_provinsi_code: address.master_provinsi_code,
            master_kota_kabupaten_code: address.master_kota_kabupaten_code,
            master_kecamatan_code: address.master_kecamatan_code,
            master_kelurahan_code: address.master_kelurahan_code,
            rt: address.rt,
            rw: address.rw,
            zip_code: address.zip_code,
          },
        ],
      },
      {
        model: 'DummyModel',
        data: [
          {
            id: null,
            locations: [
              {
                longitude: address.longitude_position,
                latitude: address.latitude_position,
              },
            ],
            fk_model: null,
          },
        ],
      },
    ],
  });
}

export function mapUserResponseToUserModel(response: TUserResponse) {
  return new MUser({
    id: response.id,
    firstName: response.first_name,
    lastName: response.last_name,
    email: response.email || response.gmail || '',
    phoneNumber: response.whatsapp,
    username: response.username,
  });
}

export function mapIndividualResponseToIndividualModel(
  response: TIndividualResponse
) {
  return new MIndividual({
    id: response.id,
    profileImage: response.profile_image_link,
    idCardImage: response.id_card_image_link,
    firstName: response.first_name,
    lastName: response.last_name,
    phoneNumber: response.whatsapp,
    nik: response.nik,
    nickname: response.nickname,
    birthplace: response.birthplace,
    bpjsHealth: response.bpjs_number,
    bpjsEmployment: response.bpjs_ketenagakerjaan_number,
    citizenship: response.citizenship,
    is_active: response.is_active,
    identityAddress: response.identity_address,
    province: response.provinsi,
    city: response.kota_kabupaten,
    subdistrict: response.kecamatan,
    village: response.kelurahan,
    provinceCode: response.master_provinsi_code,
    cityCode: response.master_kota_kabupaten_code,
    subdistrictCode: response.master_kecamatan_code,
    villageCode: response.master_kelurahan_code,
    rt: response.rt,
    rw: response.rw,

    gender: response.gender,
    birthDate: response.birthdate,
    idCardVerification: response.id_card_verification,
    bloodType: response.blood_type,
    religion: response.agama,
    maritalStatus: response.marital_status,
  });
}

export function mapContactsResponseToContactsModel(
  response: TEmergencyContactResponse[]
): MEmergencyContact[] {
  return response.map((item) => ({
    id: item.id,
    name: item.name,
    relation: item.relation as ERelation | null,
    bloodType: item.blood_type as EBloodType | null,
    bloodRhesus: item.rhesus_type as ERhesusType | null,
    emergencyNote: item.emergency_note,
    phoneNumber: item.phone_number,
    isActive: item.is_active,
    bodyAddress: item.contact_body_address,
  }));
}

export function mapUpdateBpjsRequest(
  input: TBpjsInput,
  userId: number | undefined,
  individualId: number | undefined
): TBpjsInputRequest {
  return safeParseZodObject<TBpjsInputRequest>(SBpjsInputRequest, {
    delete: [],
    upsert: [
      {
        model: 'Individual',
        data: [
          {
            id: individualId!,
            bpjs_ketenagakerjaan_number: input.bpjs_ketenagakerjaan_number,
            bpjs_number: input.bpjs_number,
            fk_model: {
              user: userId!,
            },
          },
        ],
      },
    ],
  });
}

export function mapUpdateEKTPRequest(input: TEKTPInput): TEKTPInputRequest {
  return safeParseZodObject<TEKTPInputRequest>(SEKTPInputRequest, {
    nik: input.nik,
    nickname: input.nickname,
    birth_day: input.birthdate?.getDate(),
    birth_month: input.birthdate.getMonth() + 1,
    birth_year: input.birthdate?.getFullYear(),
    birthplace: input.birthplace,
    gender: Number(input.gender),
    blood_type: Number(input.blood_type),
    marital_status: Number(input.marital_status),
    agama_int: Number(input.agama_int),
    citizenship: input.citizenship,
    identity_address: input.identity_address,
    provinsi: input.provinsi,
    rt: input.rt,
    rw: input.rw,
    kota_kabupaten: input.kota_kabupaten,
    kecamatan: input.kecamatan,
    kelurahan: input.kelurahan,
    body: {},
  });
}

export function mapEmergencyContactCreateRequest(
  input: TEmergencyContactInput,
  id: number | null,
  individualId: number | undefined
): TEmergencyContactInputRequest {
  return safeParseZodObject<TEmergencyContactInputRequest>(
    SEmergencyContactInputRequest,
    {
      delete: [],
      upsert: [
        {
          model: 'MemberEmergencyContact',
          data: [
            {
              id,
              fk_model: {
                individual: null,
              },
              relation: Number(input.relation),
              name: input.name,
              phone_number: input.phone_number,
              individual_id: individualId,
            },
          ],
        },
      ],
    }
  );
}

export async function mapKtpResponseToForm(data: TUploadKtpResponse) {
  const [rt, rw] = (data.rt_rw ?? '').split('/');
  const routesApi = RoutesApi();
  let province = '';
  let city = '';
  let subdistrict = '';
  let village = '';

  const provinceOption = await fetchLocation(
    routesApi.MARKETPLACE.GET_MASTER_LOCATION_PROVINCE,
    'province'
  );

  province = findValueFromOption(data.province ?? '', provinceOption);

  if (province) {
    const cityOption = await fetchLocation(
      routesApi.MARKETPLACE.GET_MASTER_LOCATION_CITY,
      'city',
      province
    );

    city = findValueFromOption(
      data.city?.replace(/^Kabupaten\s+/i, 'Kab. '),
      cityOption
    );
  }

  if (city) {
    const subdistrictOption = await fetchLocation(
      routesApi.MARKETPLACE.GET_MASTER_LOCATION_SUBDISTRICT,
      'subdistrict',
      city
    );

    subdistrict = findValueFromOption(data.kecamatan ?? '', subdistrictOption);
  }

  if (subdistrict) {
    const villageOption = await fetchLocation(
      routesApi.MARKETPLACE.GET_MASTER_LOCATION_VILLAGE,
      'village',
      subdistrict
    );

    village = findValueFromOption(data.kel_desa ?? '', villageOption);
  }

  const result = {
    nik: data.identity_number ?? '',
    nickname: data.fullname ?? '',
    birthdate: data.birth_date!,
    birthplace: data.birth_place ?? '',
    gender: String(getEnumValue(data.gender, EGenderEnglishLabel)),
    blood_type: String(getEnumValue(data.blood_type, EBloodTypeLabel)),
    marital_status: String(
      getEnumValue(data.marital_status, EMaritalStatusEnglishLabel)
    ),
    agama_int: String(getEnumValue(data.religion, EReligionLabel)),
    citizenship: data.nationality ?? '',
    identity_address: data.address ?? '',
    provinsi: province,
    kota_kabupaten: city,
    kecamatan: subdistrict ?? '',
    kelurahan: village ?? '',
    rt: rt ?? '',
    rw: rw ?? '',
  };
  return result;
}
